import {
  useLazyGetBuildingPackagesQuery,
  useLazyGetLayoutOptimizationsQuery,
  useLazyGetPackagesByTypeQuery,
  useLazyGetStructuralEngineeringsQuery,
} from '@consigli/hooks';
import {
  BuildingPackageType,
  DocumentPackageType,
  LayoutType,
  MeetingSecretaryType,
  QaAgentType,
  ServiceNameType,
  SolutionViewerType,
  StructuralType,
} from '@consigli/types';
import { getSolutionViewerUrl } from '@consigli/utils';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { Route } from '@/routes';

type UseServiceNavigationArgs = {
  active?: boolean;
  externalToSolutionViewer?: boolean;
  externalUrl?: string;
  id: string | undefined;
  newServiceRoute?: string;
  openInSameWindow?: boolean;
  projectId: number;
  serviceRoute?: string;
  serviceId?:
    | DocumentPackageType
    | LayoutType
    | StructuralType
    | BuildingPackageType
    | MeetingSecretaryType
    | SolutionViewerType
    | QaAgentType;
};

/**
 * The Solution Viewer pages that cards flagged `externalToSolutionViewer` open,
 * by service. Anything not listed here — Solution Viewer itself — opens the root.
 */
const SOLUTION_VIEWER_ROUTES: Readonly<Record<string, string>> = {
  [ServiceNameType.DATA_CENTER]: '/datacenter',
  [ServiceNameType.WASTEWATER_TREATMENT]: '/water_treatment',
};

/**
 * Where one service card goes when it is activated.
 *
 * Lifted out of `pages/service-list/service-card.tsx`, with one deliberate
 * change: `newServiceRoute` is made absolute before it reaches `navigate`. The
 * legacy card only ever rendered under `/projects/:id/services`, where the
 * relative `<slug>/create` resolved correctly; the Agents tab renders the same
 * hook from the two-segment `/projects/:id/agents` leaf, where the same string
 * resolved to `/projects/:id/agents/<slug>/create` and 404'd.
 *
 * The external-URL and Solution Viewer branches open a window and never
 * navigate, while the rest resolve a package id first, so the two halves are
 * inseparable and sit behind one callback.
 *
 * `projectId` is an argument rather than a `useProjectId()` call so the hook works
 * for any caller holding a project id, card-by-card, without a route match.
 */
export const useServiceNavigation = ({
  active,
  externalToSolutionViewer,
  externalUrl,
  id,
  newServiceRoute,
  openInSameWindow,
  projectId,
  serviceRoute,
  serviceId,
}: UseServiceNavigationArgs): { navigateToService: () => Promise<void> } => {
  const navigate = useNavigate();
  const [getPackages] = useLazyGetPackagesByTypeQuery();
  const [getLayouts] = useLazyGetLayoutOptimizationsQuery();
  const [getStructural] = useLazyGetStructuralEngineeringsQuery();
  const [getBuildingPackages] = useLazyGetBuildingPackagesQuery();

  /** The destination, or undefined when the config gives the card nowhere to go. */
  const redirectUser = useCallback(async (): Promise<string | undefined> => {
    if (!serviceId || !serviceRoute || !newServiceRoute) return undefined;

    // Absolute on purpose: the hook renders from both /projects/:id/services
    // and the /projects/:id/agents leaf, and react-router resolves a relative
    // path against whichever route is current. service-list-route.tsx makes the
    // same point about `..` on these two-segment routes.
    const servicesBase = `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}`;
    const newServiceUrl = `${servicesBase}/${newServiceRoute}`;
    if (!active) return newServiceUrl;

    const packageUrl = (packageId: string, page: Route) =>
      `${servicesBase}/${serviceRoute}/${Route.PACKAGES}/${packageId}/${page}`;

    const fetchPackagesAndBuildUrl = async () => {
      try {
        if (Object.values(DocumentPackageType).some((type) => type === serviceId)) {
          const packages = await getPackages({
            projectId,
            type: serviceId as DocumentPackageType,
          }).unwrap();

          if (packages.length === 0) return newServiceUrl;

          // Use comparison page for schedule check
          if (serviceId === DocumentPackageType.SCHEDULE_CHECK) {
            return packageUrl(packages[0].id, Route.COMPARISON);
          }

          // Use reports page for site due diligence (no dashboard)
          if (serviceId === DocumentPackageType.SITE_DUE_DILIGENCE) {
            return packageUrl(packages[0].id, Route.REPORTS);
          }

          return packageUrl(packages[0].id, Route.DASHBOARD);
        }

        if (serviceId === StructuralType.STRUCTURAL) {
          const packages = await getStructural({
            projectId,
            type: StructuralType.STRUCTURAL,
          }).unwrap();

          return packages.length > 0 ? packageUrl(packages[0].id, Route.DASHBOARD) : newServiceUrl;
        }

        if (Object.values(BuildingPackageType).some((type) => type === serviceId)) {
          const response = await getBuildingPackages({
            projectId,
            type: serviceId as BuildingPackageType,
            page: 'all',
          }).unwrap();

          const packages = response.results;
          return packages.length > 0 ? packageUrl(packages[0].id, Route.DASHBOARD) : newServiceUrl;
        }

        if (serviceId === MeetingSecretaryType.MEETING_SECRETARY) {
          return newServiceUrl;
        }

        if (serviceId === QaAgentType.QA_AGENT) {
          return newServiceUrl;
        }

        // Default to LayoutType. SolutionViewerType is in the args union but has
        // no branch: navigateToService intercepts it via externalToSolutionViewer.
        const packages = await getLayouts({
          projectId,
          type: serviceId as LayoutType,
        }).unwrap();

        return packages.length > 0 ? packageUrl(packages[0].id, Route.DASHBOARD) : newServiceUrl;
      } catch (error) {
        console.error('Error fetching packages:', error);
        return newServiceUrl;
      }
    };

    return fetchPackagesAndBuildUrl();
  }, [
    active,
    getBuildingPackages,
    getLayouts,
    getPackages,
    getStructural,
    newServiceRoute,
    projectId,
    serviceId,
    serviceRoute,
  ]);

  const navigateToService = async () => {
    if (externalUrl) {
      const urlWithProject = `${externalUrl}?projectId=${projectId}`;
      window.open(urlWithProject, openInSameWindow ? '_self' : '_blank');
      return;
    }
    if (externalToSolutionViewer) {
      const base = getSolutionViewerUrl();
      // Data Center and Wastewater treatment target their own routes; Solution
      // Viewer opens the root.
      const route = id === undefined ? undefined : SOLUTION_VIEWER_ROUTES[id];
      const url = route ? `${base}${route}` : base;
      // Add project ID as URL parameter when navigating to solution viewer
      const urlWithProject = `${url}?projectId=${projectId}`;
      window.open(urlWithProject, '_blank');
      return;
    }
    const to = await redirectUser();
    // Incomplete config has nowhere to go. Not navigate(''): react-router
    // resolves that to the current URL and pushes a duplicate history entry.
    if (to === undefined) return;
    navigate(to);
  };

  return { navigateToService };
};
